import { useState, useEffect, useCallback } from 'react';
import {
  Brain, Database, Search, Zap, ChevronDown,
  Sparkles, ArrowLeft, RefreshCw, Check, X, HardDrive,
  MessageSquare, Clock, Tag, Archive, Layers, TrendingUp
} from 'lucide-react';
import { api } from '../../lib/api';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

interface MemoryLayerUsage {
  layerId: string;
  layerType: string;
  currentChars: number;
  maxChars: number;
  percentUsed: number;
  entryCount: number;
}

interface SessionArchiveResult {
  id: string;
  threadId: string;
  summary: string;
  keyTopics: string[];
  messageCount: number;
  archivedAt: string;
}

interface SkillCandidate {
  id: string;
  name: string;
  description: string;
  triggerPattern: string;
  confidence: number;
  occurrences: number;
  status: 'pending' | 'approved' | 'dismissed';
}

interface IntelligenceTabProps {
  replicaId: string;
  onBack?: () => void;
}

// ═══════════════════════════════════════════════════════════════════════
// MOCK DATA (used when API routes are not yet wired)
// ═══════════════════════════════════════════════════════════════════════

const MOCK_MEMORY: MemoryLayerUsage[] = [
  { layerId: '1', layerType: 'CORE_PERSONAL', currentChars: 12840, maxChars: 50000, percentUsed: 25.7, entryCount: 42 },
  { layerId: '2', layerType: 'SHARED', currentChars: 3200, maxChars: 25000, percentUsed: 12.8, entryCount: 15 },
  { layerId: '3', layerType: 'ENTERPRISE_DERIVATIVE', currentChars: 0, maxChars: 100000, percentUsed: 0, entryCount: 0 },
];

const MOCK_SKILLS: SkillCandidate[] = [
  { id: '1', name: 'Meeting Summary', description: 'Automatically summarize meeting notes with action items', triggerPattern: 'User frequently corrects summary format', confidence: 0.87, occurrences: 12, status: 'pending' },
  { id: '2', name: 'French Email Draft', description: 'Draft professional emails in French following company tone', triggerPattern: 'Repeated corrections to email phrasing', confidence: 0.72, occurrences: 8, status: 'pending' },
  { id: '3', name: 'Code Review Style', description: 'Follow team code review conventions with inline comments', triggerPattern: 'Consistent feedback on code review format', confidence: 0.65, occurrences: 5, status: 'pending' },
];

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

export default function IntelligenceTab({ replicaId, onBack }: IntelligenceTabProps) {
  // ── State ──
  const [memoryLayers, setMemoryLayers] = useState<MemoryLayerUsage[]>(MOCK_MEMORY);
  const [skillCandidates, setSkillCandidates] = useState<SkillCandidate[]>(MOCK_SKILLS);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SessionArchiveResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchTime, setSearchTime] = useState<number | null>(null);
  const [memoryExpanded, setMemoryExpanded] = useState(true);
  const [skillsExpanded, setSkillsExpanded] = useState(true);
  const [searchExpanded, setSearchExpanded] = useState(true);
  const [consolidating, setConsolidating] = useState<string | null>(null);

  // ── Fetch memory usage ──
  useEffect(() => {
    api.get<{ success: boolean; layers: MemoryLayerUsage[] }>(`/memory-layers/${replicaId}/usage`)
      .then(res => {
        if (res.data?.success && res.data.layers) {
          setMemoryLayers(res.data.layers);
        }
      })
      .catch(() => { /* fallback to mock */ });
  }, [replicaId]);

  // ── Fetch skill candidates ──
  useEffect(() => {
    api.get<{ success: boolean; candidates: SkillCandidate[] }>(`/auto-skills/${replicaId}/candidates`)
      .then(res => {
        if (res.data?.success && res.data.candidates) {
          setSkillCandidates(res.data.candidates);
        }
      })
      .catch(() => { /* fallback to mock */ });
  }, [replicaId]);

  // ── Session search ──
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    const start = Date.now();
    try {
      const res = await api.get<{
        success: boolean;
        results: SessionArchiveResult[];
        totalFound: number;
      }>(`/session-archives/${replicaId}/search?q=${encodeURIComponent(searchQuery)}`);
      if (res.data?.success) {
        setSearchResults(res.data.results || []);
      }
    } catch {
      // Show empty results on error
      setSearchResults([]);
    }
    setSearchTime(Date.now() - start);
    setSearching(false);
  }, [replicaId, searchQuery]);

  // ── Memory consolidation ──
  const handleConsolidate = async (layerId: string) => {
    setConsolidating(layerId);
    try {
      await api.post(`/memory-layers/${layerId}/consolidate`);
      // Refresh usage after consolidation
      const res = await api.get<{ success: boolean; layers: MemoryLayerUsage[] }>(`/memory-layers/${replicaId}/usage`);
      if (res.data?.success && res.data.layers) {
        setMemoryLayers(res.data.layers);
      }
    } catch {
      /* silent */
    }
    setConsolidating(null);
  };

  // ── Skill actions ──
  const handleSkillAction = (skillId: string, action: 'approved' | 'dismissed') => {
    setSkillCandidates(prev =>
      prev.map(s => s.id === skillId ? { ...s, status: action } : s)
    );
    // Would call API: POST /auto-skills/:replicaId/candidates/:skillId/action
  };

  // ── Layer type label ──
  const layerLabel = (type: string) => {
    const labels: Record<string, { label: string; icon: string; color: string }> = {
      CORE_PERSONAL: { label: 'Core Personal', icon: '🧠', color: 'from-blue-500 to-indigo-500' },
      SHARED: { label: 'Shared', icon: '🤝', color: 'from-emerald-500 to-teal-500' },
      ENTERPRISE_DERIVATIVE: { label: 'Enterprise', icon: '🏢', color: 'from-purple-500 to-violet-500' },
    };
    return labels[type] || { label: type, icon: '📦', color: 'from-gray-500 to-gray-600' };
  };

  // ── Bar color by usage ──
  const usageColor = (pct: number) => {
    if (pct >= 90) return 'from-red-500 to-red-400';
    if (pct >= 70) return 'from-amber-500 to-orange-400';
    if (pct >= 40) return 'from-blue-500 to-blue-400';
    return 'from-emerald-500 to-emerald-400';
  };

  const pendingSkills = skillCandidates.filter(s => s.status === 'pending');

  return (
    <div className="max-w-4xl mx-auto space-y-5 p-4 animate-fade-in">
      {/* Header */}
      {onBack && (
        <div className="flex items-center gap-3 mb-2">
          <button onClick={onBack} className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary rounded-xl transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-hig-text-primary flex items-center gap-2">
              <Brain size={22} className="text-hig-accent" />
              Intelligence Center
            </h1>
            <p className="text-xs text-hig-text-tertiary">Memory · Learning · Cross-Session Search</p>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SECTION 1: MEMORY CAPACITY (M2)                                    */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="card overflow-hidden">
        <button
          onClick={() => setMemoryExpanded(!memoryExpanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-hig-bg-elevated/30 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center">
              <Database size={16} className="text-blue-400" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-hig-text-primary">Bounded Memory</p>
              <p className="text-[11px] text-hig-text-tertiary">{memoryLayers.length} layers · capacity managed</p>
            </div>
          </div>
          <ChevronDown size={14} className={`text-hig-text-tertiary transition-transform duration-300 ${memoryExpanded ? 'rotate-180' : ''}`} />
        </button>

        <div className={`collapsible-drawer ${memoryExpanded ? 'open' : ''}`}>
          <div className="drawer-content">
            <div className="border-t border-hig-divider p-4 space-y-4">
              {memoryLayers.map((layer) => {
                const meta = layerLabel(layer.layerType);
                return (
                  <div key={layer.layerId} className="bg-hig-bg-tertiary/30 rounded-xl p-4 hover:bg-hig-bg-tertiary/50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{meta.icon}</span>
                        <span className="text-sm font-medium text-hig-text-primary">{meta.label}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-hig-bg-tertiary text-hig-text-tertiary">
                          {layer.entryCount} entries
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-hig-text-secondary font-mono">
                          {(layer.currentChars / 1000).toFixed(1)}k / {(layer.maxChars / 1000).toFixed(0)}k
                        </span>
                        {layer.percentUsed > 60 && (
                          <button
                            onClick={() => handleConsolidate(layer.layerId)}
                            disabled={consolidating === layer.layerId}
                            className="text-[10px] px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                          >
                            {consolidating === layer.layerId ? <RefreshCw size={10} className="animate-spin" /> : 'Consolidate'}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="w-full bg-hig-bg-tertiary rounded-full h-2 overflow-hidden">
                      <div
                        className={`bg-gradient-to-r ${usageColor(layer.percentUsed)} h-2 rounded-full transition-all duration-1000 ease-out`}
                        style={{ width: `${Math.min(layer.percentUsed, 100)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-hig-text-tertiary mt-1.5 text-right">
                      {layer.percentUsed.toFixed(1)}% used
                    </p>
                  </div>
                );
              })}

              {/* Total summary */}
              <div className="flex items-center justify-between px-1 pt-1">
                <div className="flex items-center gap-2 text-xs text-hig-text-tertiary">
                  <Layers size={13} />
                  <span>Total: {(memoryLayers.reduce((s, l) => s + l.currentChars, 0) / 1000).toFixed(1)}k chars across {memoryLayers.length} layers</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                  <HardDrive size={12} />
                  <span>Healthy</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SECTION 2: SESSION SEARCH (M3)                                     */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="card overflow-hidden">
        <button
          onClick={() => setSearchExpanded(!searchExpanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-hig-bg-elevated/30 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
              <Archive size={16} className="text-cyan-400" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-hig-text-primary">Cross-Session Memory</p>
              <p className="text-[11px] text-hig-text-tertiary">Search across all past conversations</p>
            </div>
          </div>
          <ChevronDown size={14} className={`text-hig-text-tertiary transition-transform duration-300 ${searchExpanded ? 'rotate-180' : ''}`} />
        </button>

        <div className={`collapsible-drawer ${searchExpanded ? 'open' : ''}`}>
          <div className="drawer-content">
            <div className="border-t border-hig-divider p-4 space-y-3">
              {/* Search bar */}
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-hig-text-tertiary" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search past conversations... (e.g. 'project budget Q2')"
                  className="w-full pl-9 pr-20 py-2.5 bg-hig-bg-tertiary border border-hig-divider rounded-xl text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:border-hig-accent/50 focus:ring-1 focus:ring-hig-accent/20 transition-all"
                />
                <button
                  onClick={handleSearch}
                  disabled={searching || !searchQuery.trim()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 rounded-lg text-xs font-medium bg-hig-accent/10 text-hig-accent hover:bg-hig-accent/20 disabled:opacity-40 transition-colors"
                >
                  {searching ? <RefreshCw size={12} className="animate-spin" /> : 'Search'}
                </button>
              </div>

              {/* Search results */}
              {searchTime !== null && (
                <p className="text-[11px] text-hig-text-tertiary">
                  {searchResults.length} results in {searchTime}ms
                </p>
              )}

              {searchResults.length > 0 ? (
                <div className="space-y-2">
                  {searchResults.map((result) => (
                    <div key={result.id} className="bg-hig-bg-tertiary/30 rounded-xl p-3.5 hover:bg-hig-bg-tertiary/50 transition-colors cursor-pointer">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-hig-text-primary font-medium leading-snug line-clamp-2">{result.summary}</p>
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <div className="flex items-center gap-1 text-[11px] text-hig-text-tertiary">
                              <MessageSquare size={11} />
                              <span>{result.messageCount} msgs</span>
                            </div>
                            <div className="flex items-center gap-1 text-[11px] text-hig-text-tertiary">
                              <Clock size={11} />
                              <span>{new Date(result.archivedAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                          {result.keyTopics.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {result.keyTopics.slice(0, 4).map((topic) => (
                                <span key={topic} className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/15">
                                  {topic}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchTime !== null && (
                <div className="bg-hig-bg-tertiary/20 rounded-xl p-6 text-center">
                  <Search size={24} className="mx-auto text-hig-text-tertiary mb-2 opacity-40" />
                  <p className="text-sm text-hig-text-secondary">No matches found</p>
                  <p className="text-[11px] text-hig-text-tertiary mt-1">Try different keywords or phrases</p>
                </div>
              )}

              {searchTime === null && (
                <div className="bg-hig-bg-tertiary/20 rounded-xl p-6 text-center">
                  <Archive size={24} className="mx-auto text-cyan-400/30 mb-2" />
                  <p className="text-xs text-hig-text-tertiary">Search across all archived conversations to find context from past sessions</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SECTION 3: AUTO-DETECTED SKILLS (M1)                               */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="card overflow-hidden">
        <button
          onClick={() => setSkillsExpanded(!skillsExpanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-hig-bg-elevated/30 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
              <Sparkles size={16} className="text-amber-400" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-hig-text-primary">Auto-Detected Skills</p>
              <p className="text-[11px] text-hig-text-tertiary">
                {pendingSkills.length > 0
                  ? `${pendingSkills.length} skill${pendingSkills.length > 1 ? 's' : ''} suggested from your corrections`
                  : 'Learning from your corrections'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pendingSkills.length > 0 && (
              <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold flex items-center justify-center">
                {pendingSkills.length}
              </span>
            )}
            <ChevronDown size={14} className={`text-hig-text-tertiary transition-transform duration-300 ${skillsExpanded ? 'rotate-180' : ''}`} />
          </div>
        </button>

        <div className={`collapsible-drawer ${skillsExpanded ? 'open' : ''}`}>
          <div className="drawer-content">
            <div className="border-t border-hig-divider p-4 space-y-3">
              {skillCandidates.length === 0 ? (
                <div className="bg-hig-bg-tertiary/20 rounded-xl p-6 text-center">
                  <Zap size={24} className="mx-auto text-amber-400/30 mb-2" />
                  <p className="text-sm text-hig-text-secondary">No skills detected yet</p>
                  <p className="text-[11px] text-hig-text-tertiary mt-1">
                    As you correct your Replica, patterns will be detected and suggested as new skills
                  </p>
                </div>
              ) : (
                skillCandidates.map((skill) => (
                  <div
                    key={skill.id}
                    className={`bg-hig-bg-tertiary/30 rounded-xl p-4 transition-all duration-300 ${
                      skill.status === 'approved' ? 'opacity-60 border border-emerald-500/20' :
                      skill.status === 'dismissed' ? 'opacity-30' : 'hover:bg-hig-bg-tertiary/50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Zap size={14} className="text-amber-400 flex-shrink-0" />
                          <p className="text-sm font-medium text-hig-text-primary">{skill.name}</p>
                          {skill.status === 'approved' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium">Approved</span>
                          )}
                          {skill.status === 'dismissed' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-hig-bg-tertiary text-hig-text-tertiary font-medium">Dismissed</span>
                          )}
                        </div>
                        <p className="text-xs text-hig-text-secondary leading-relaxed mb-2">{skill.description}</p>
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex items-center gap-1 text-[11px] text-hig-text-tertiary">
                            <TrendingUp size={11} />
                            <span>{Math.round(skill.confidence * 100)}% confidence</span>
                          </div>
                          <div className="flex items-center gap-1 text-[11px] text-hig-text-tertiary">
                            <Tag size={11} />
                            <span>{skill.occurrences} occurrences</span>
                          </div>
                        </div>
                      </div>

                      {skill.status === 'pending' && (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => handleSkillAction(skill.id, 'approved')}
                            className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                            title="Approve skill"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => handleSkillAction(skill.id, 'dismissed')}
                            className="p-1.5 rounded-lg bg-hig-bg-tertiary text-hig-text-tertiary hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Dismiss"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
